// Member profile → original booking request backlink (reverse edge of the
// submission dialog's existing member link). Fetches the member-scoped
// submission list; a 403 (org without the booking_requests entitlement) or an
// ownership-scope mismatch resolves to [] and the section renders nothing —
// never a dead link. Card markup mirrors the profile page's local <Section>.
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Calendar, ChevronRight, FileText } from "lucide-react";
import type { BookingRequestSubmission } from "@shared/schema";
import { bookingRejectionCategoryLabel } from "@shared/booking-rejection-categories";

const STATUS_BADGE: Record<string, string> = {
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
  archived: "bg-gray-100 text-gray-500",
  pending: "bg-amber-100 text-amber-700",
};

export function MemberBookingRequestSection({ memberId }: { memberId: number }) {
  const query = useQuery<BookingRequestSubmission[]>({
    queryKey: ["/api/app/booking-requests/submissions", { memberId }],
    queryFn: async () => {
      const res = await fetch(`/api/app/booking-requests/submissions?memberId=${memberId}`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
  });

  const submissions = query.data ?? [];
  if (submissions.length === 0) return null;
  // Newest first from the server; a member can have several (rejected then
  // re-applied, or a different template) — link the latest, count the rest.
  const latest = submissions[0];

  return (
    <div className="border border-gray-200 rounded-lg p-4" data-testid="section-member-booking-request">
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
        <FileText className="h-4 w-4" />
        <h3 className="text-sm font-semibold text-gray-700">Booking Request</h3>
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-500 w-28">Submitted</span>
          <span className="text-sm text-gray-900" data-testid="text-member-booking-submitted">
            {new Date(latest.submittedAt).toLocaleDateString()}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500 w-28 ml-6">Status</span>
          <Badge className={STATUS_BADGE[latest.status] ?? STATUS_BADGE.pending} data-testid="badge-member-booking-status">
            {latest.status}
          </Badge>
        </div>
        {latest.status === "rejected" && (latest.rejectionCategory || latest.reviewNotes) && (
          <p className="text-xs text-gray-600 ml-6" data-testid="text-member-booking-reason">
            {latest.rejectionCategory ? `${bookingRejectionCategoryLabel(latest.rejectionCategory)}${latest.reviewNotes ? ": " : ""}` : ""}
            {latest.reviewNotes ?? ""}
          </p>
        )}
        <Link
          href={`/app/members/booking-requests?submission=${latest.id}`}
          className="flex items-center gap-1 text-xs text-brand hover:underline"
          data-testid="link-member-booking-request"
        >
          View original booking request <ChevronRight className="h-3 w-3" />
        </Link>
        {submissions.length > 1 && (
          <p className="text-xs text-gray-400" data-testid="text-member-booking-earlier">
            +{submissions.length - 1} earlier request{submissions.length - 1 === 1 ? "" : "s"}
          </p>
        )}
      </div>
    </div>
  );
}
